import {
  addDays,
  addMonths,
  FARM_TIME_ZONE,
  maxDate,
  recordIds,
  todayInTimeZone,
  type ReminderType,
  type TenantSettings,
} from '@dairy/shared';
import { eventsOf } from './events';
import { isCalfAge, type Stage } from './stage';
import type { DomainAnimal, DomainEvent, DomainProtocol } from './types';

/** HE-4 target groups (sprint 3 §6). */
export function protocolApplies(
  protocol: DomainProtocol,
  animal: DomainAnimal,
  stage: Stage,
  today: string,
  s: Pick<TenantSettings, 'calfMaxAgeMonths'>,
): boolean {
  if (!protocol.active || protocol.deletedAt) return false;
  if (animal.deletedAt || animal.status !== 'ACTIVE') return false;
  switch (protocol.target) {
    case 'ALL':
      return true;
    case 'ADULT_FEMALES':
      return animal.sex === 'FEMALE' && !isCalfAge(animal.birthDate, today, s);
    case 'YOUNGSTOCK':
      return stage === 'CALF';
  }
}

export function protocolReminderType(protocol: Pick<DomainProtocol, 'type'>): ReminderType {
  return protocol.type === 'VACCINATION' ? 'VACCINATION_DUE' : 'DEWORMING_DUE';
}

function localDateOf(timestamp: string | undefined): string | null {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  return Number.isNaN(d.getTime()) ? null : todayInTimeZone(FARM_TIME_ZONE, d);
}

/**
 * First due date when the animal has no event of this protocol (D-5):
 * birth + firstDueAgeDays; if not set, "today" — taken as the day the protocol started to apply to the animal
 * (protocol created, animal registered or arrived, or entered the target group), so the date stays stable.
 */
export function firstProtocolDue(
  protocol: DomainProtocol,
  animal: DomainAnimal,
  today: string,
  s: Pick<TenantSettings, 'calfMaxAgeMonths'>,
): string {
  if (protocol.firstDueAgeDays !== null && protocol.firstDueAgeDays !== undefined) {
    return addDays(animal.birthDate, protocol.firstDueAgeDays);
  }
  const groupEntry =
    protocol.target === 'ADULT_FEMALES' ? addMonths(animal.birthDate, s.calfMaxAgeMonths) : animal.birthDate;
  const start = localDateOf(protocol.clientCreatedAt) ?? today;
  return maxDate(groupEntry, animal.purchaseDate, localDateOf(animal.clientCreatedAt), start) ?? today;
}

export interface ProtocolDue {
  dueDate: string;
  /** Earlier reminders closed by an event dated on or after (due − 14 days). */
  resolved: string[];
}

export const PROTOCOL_EARLY_DAYS = 14;

/** HE-4 / D-5: the protocol's chain of due dates for one animal; the last one is open. */
export function nextProtocolDue(
  protocol: DomainProtocol,
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  today: string,
  s: Pick<TenantSettings, 'calfMaxAgeMonths'>,
): ProtocolDue {
  const evs = eventsOf(events, animal.id).filter(
    (e) => e.type === protocol.type && (e.data as { protocolId?: string }).protocolId === protocol.id,
  );
  let due = firstProtocolDue(protocol, animal, today, s);
  const resolved: string[] = [];
  for (const e of evs) {
    if (e.date >= addDays(due, -PROTOCOL_EARLY_DAYS)) resolved.push(due);
    due = addDays(e.date, protocol.intervalDays);
  }
  return { dueDate: due, resolved };
}

export function protocolReminderId(animalId: string, protocol: DomainProtocol, dueDate: string): string {
  return recordIds.protocolReminder(animalId, protocolReminderType(protocol), protocol.id, dueDate);
}
